import { useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { matchNav, type NavChild, type NavItem } from './navigation'
import { useVisibleNav } from './useNavigationTree'
import { cn } from '@/lib/utils'
import { HumisLogo } from '@/components/brand/HumisLogo'

/**
 * The 240px sidebar from the reference design.
 *
 * <p>Modules with children expand in place rather than navigating: the destination is
 * always one of the children, and a parent that both navigates and toggles makes the click
 * ambiguous. One module is open at a time — a fully expanded sidebar of every module's
 * children is a wall of forty links, and the point of grouping was to avoid that.
 *
 * <p>Collapsed to icons, the children move into a hover flyout, because an icon rail has
 * nowhere to put them and hiding them entirely would make the collapsed state a different,
 * smaller product.
 */
export function Sidebar({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation()
  const location = useLocation()
  const modules = useVisibleNav()

  const activeModuleKey = matchNav(location.pathname)?.module.key
  const [openKey, setOpenKey] = useState<string | undefined>(activeModuleKey)
  const [lastRouteModule, setLastRouteModule] = useState(activeModuleKey)

  // Follow the route: arriving at a child of another module — from a link on the page, a
  // bookmark, the back button — opens that module, rather than leaving the sidebar pointing
  // somewhere the user is not. Adjusted during render rather than in an effect, so the
  // sidebar never paints the previous module's state for a frame first.
  if (activeModuleKey !== lastRouteModule) {
    setLastRouteModule(activeModuleKey)
    if (activeModuleKey) {
      setOpenKey(activeModuleKey)
    }
  }

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-e border-[var(--color-border)] bg-[var(--color-sidebar)] transition-[width] duration-200',
        collapsed ? 'w-[var(--spacing-sidebar-collapsed)]' : 'w-[var(--spacing-sidebar)]',
      )}
    >
      {/* --- brand --- */}
      <div
        className={cn(
          'flex h-[var(--spacing-topbar)] items-center px-4',
          collapsed && 'justify-center px-0',
        )}
      >
        {collapsed ? (
          <HumisLogo variant="mark" alt={t('app.name')} className="size-9 shrink-0" />
        ) : (
          <HumisLogo variant="nav" alt={t('app.name')} className="h-8 w-auto max-w-full" />
        )}
      </div>

      {/* --- navigation --- */}
      <nav aria-label="Main navigation" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2">
        <ul className="space-y-0.5">
          {modules.map((module) =>
            module.children ? (
              <ModuleGroup
                key={module.key}
                module={module}
                collapsed={collapsed}
                open={openKey === module.key}
                active={activeModuleKey === module.key}
                onToggle={() => setOpenKey((current) => (current === module.key ? undefined : module.key))}
                onNavigate={onNavigate}
              />
            ) : (
              <li key={module.key}>
                <ModuleLink module={module} collapsed={collapsed} onNavigate={onNavigate} />
              </li>
            ),
          )}
        </ul>
      </nav>

    </aside>
  )
}

/** A module that navigates straight to a page. */
function ModuleLink({
  module,
  collapsed,
  onNavigate,
}: {
  module: NavItem
  collapsed: boolean
  onNavigate?: () => void
}) {
  const { t } = useTranslation()
  const Icon = module.icon

  return (
    <NavLink
      to={module.to ?? '/'}
      end={module.to === '/'}
      onClick={onNavigate}
      title={t(`nav.${module.key}`)}
      className={({ isActive }) => rowClass(isActive)}
    >
      <Icon className="size-[18px] shrink-0" aria-hidden="true" />
      {!collapsed && <span className={LABEL_CLASS}>{t(`nav.${module.key}`)}</span>}
    </NavLink>
  )
}

/** A module that holds children. Expands in place, or flies out when collapsed. */
function ModuleGroup({
  module,
  collapsed,
  open,
  active,
  onToggle,
  onNavigate,
}: {
  module: NavItem
  collapsed: boolean
  open: boolean
  active: boolean
  onToggle: () => void
  onNavigate?: () => void
}) {
  const { t } = useTranslation()
  const Icon = module.icon
  const label = t(`nav.${module.key}`)
  const panelId = `nav-panel-${module.key}`

  if (collapsed) {
    return <CollapsedModule module={module} active={active} onNavigate={onNavigate} />
  }

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        title={label}
        className={cn(rowClass(active && !open), 'w-full text-start')}
      >
        <Icon className="size-[18px] shrink-0" aria-hidden="true" />
        <span className={LABEL_CLASS}>{label}</span>
        <ChevronRight
          className={cn(
            'size-4 shrink-0 opacity-40 transition-transform duration-150',
            open ? 'rotate-90' : 'rtl-flip',
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul id={panelId} className="mt-0.5 space-y-0.5 ps-4">
          {module.children?.map((child) => (
            <li key={child.key}>
              <ChildLink child={child} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * A module in the icon rail, with its children in a flyout.
 *
 * <p>The flyout is positioned {@code fixed} against the row's measured position rather than
 * absolutely inside it. The nav is a scroll container — a long menu has to scroll — and an
 * absolutely positioned child of a scroll container is clipped by it, so a purely CSS
 * flyout appeared to work in a visibility assertion while being invisible on screen.
 */
function CollapsedModule({
  module,
  active,
  onNavigate,
}: {
  module: NavItem
  active: boolean
  onNavigate?: () => void
}) {
  const { t } = useTranslation()
  const Icon = module.icon
  const label = t(`nav.${module.key}`)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)

  function show() {
    const rect = rowRef.current?.getBoundingClientRect()
    if (rect) {
      setAnchor({ top: rect.top, left: rect.right + 4 })
    }
  }

  return (
    <li>
      <div
        ref={rowRef}
        onMouseEnter={show}
        onMouseLeave={() => setAnchor(null)}
        onFocus={show}
        onBlur={(event) => {
          // Only when focus leaves the row and its flyout entirely, or tabbing into a child
          // would close the menu that holds it.
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setAnchor(null)
          }
        }}
      >
        <span className={cn(rowClass(active), 'cursor-default justify-center')} title={label}>
          <Icon className="size-[18px] shrink-0" aria-hidden="true" />
        </span>

        {anchor && (
          <div
            style={{ top: anchor.top, insetInlineStart: anchor.left }}
            className={cn(
              'fixed z-50 min-w-52 rounded-[var(--radius-card)] border border-[var(--color-border)]',
              'bg-[var(--color-card)] p-2 shadow-lg',
            )}
          >
            <p className="px-2 pb-1 text-xs font-semibold text-[var(--color-muted)]">{label}</p>
            <ul className="space-y-0.5">
              {module.children?.map((child) => (
                <li key={child.key}>
                  <ChildLink child={child} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </li>
  )
}

function ChildLink({ child, onNavigate }: { child: NavChild; onNavigate?: () => void }) {
  const { t } = useTranslation()

  return (
    <NavLink
      to={child.to}
      end
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded-[8px] px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-[var(--color-primary-soft)] font-medium text-[var(--color-primary)]'
            : 'text-[var(--color-body)] hover:bg-[var(--color-page)]',
        )
      }
    >
      <span className={LABEL_CLASS} title={t(`navChild.${child.key}`)}>
        {t(`navChild.${child.key}`)}
      </span>
    </NavLink>
  )
}

/**
 * Nav labels wrap rather than truncate.
 *
 * <p>They used to be clipped — "Employee Man…", "Document Man…" — which reads as a broken
 * layout and, worse, makes two modules look alike at the point where they stop. The sidebar
 * is now wide enough for the longest label in both languages on one line (measured, not
 * guessed); wrapping to a second line is the fallback if a translation grows, and the full
 * text is on the element's `title` either way.
 */
const LABEL_CLASS = 'flex-1 min-w-0 whitespace-normal break-words leading-tight line-clamp-2'

function rowClass(isActive: boolean): string {
  return cn(
    'flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-colors',
    isActive
      ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
      : 'text-[var(--color-body)] hover:bg-[var(--color-page)]',
  )
}
